import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../../lib/firebase";
import {
  addDoc,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function fmtTime(ts) {
  try {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function SupportChatScreen({ goBack }) {
  const u = auth.currentUser;

  const meName = useMemo(() => safe(u?.displayName) || safe(u?.email) || "User", [u]);
  const meEmail = useMemo(() => safe(u?.email), [u]);
  const uid = u?.uid || "";

  const [loading, setLoading] = useState(true);
  const [ticketId, setTicketId] = useState("");
  const [ticket, setTicket] = useState(null);

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [subject, setSubject] = useState("Support");
  const [starting, setStarting] = useState(false);

  const [err, setErr] = useState("");

  const bottomRef = useRef(null);

  // 1) Find latest active ticket for this user (OPEN or PENDING)
  useEffect(() => {
    if (!uid) return;

    setLoading(true);
    setErr("");

    // NOTE: Firestore doesn't support OR easily without extra libs,
    // so simplest: just read latest ticket and continue chatting there.
    // If you want strict OPEN/PENDING only, create "active: true" on ticket and query active==true.
    const q = query(
      collection(db, "supportTickets"),
      where("uid", "==", uid),
      orderBy("lastMessageAt", "desc"),
      limit(1)
    );

    const unsub = onSnapshot(
      q,
      async (snap) => {
        if (snap.empty) {
          setTicketId("");
          setTicket(null);
          setMessages([]);
          setLoading(false);
          return;
        }
        const d = snap.docs[0];
        setTicketId(d.id);
        setTicket({ id: d.id, ...d.data() });
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setErr("Failed to load support chat (index/rules?).");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  // 2) Listen to messages for the selected ticket
  useEffect(() => {
    if (!ticketId) return;

    const mq = query(
      collection(db, "supportTickets", ticketId, "messages"),
      orderBy("createdAt", "asc"),
      limit(300)
    );

    const unsub = onSnapshot(
      mq,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMessages(rows);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
      },
      (e) => {
        console.error(e);
        setErr("Failed to load messages (rules/index?).");
      }
    );

    return () => unsub();
  }, [ticketId]);

  async function startNewTicket() {
    setErr("");
    const s = safe(subject) || "Support";

    if (!uid) {
      setErr("You must be logged in.");
      return;
    }

    try {
      setStarting(true);

      // Create ticket doc first
      const ticketRef = doc(collection(db, "supportTickets"));
      const id = ticketRef.id;

      await setDoc(ticketRef, {
        uid,
        name: meName,
        email: meEmail,
        subject: s,
        status: "OPEN",
        assignedTo: "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageText: "Ticket started",
      });

      // Optional: create a system message so chat isn't empty
      await addDoc(collection(db, "supportTickets", id, "messages"), {
        from: "SYSTEM",
        text: `Ticket created: ${s}`,
        createdAt: serverTimestamp(),
      });

      setTicketId(id);
      setTicket({ id, subject: s, status: "OPEN" });
    } catch (e) {
      console.error(e);
      setErr("Failed to start ticket (rules/index?).");
    } finally {
      setStarting(false);
    }
  }

  async function send() {
    setErr("");
    const t = safe(text);
    if (!t) return;

    if (!uid) {
      setErr("You must be logged in.");
      return;
    }

    try {
      // If no ticket yet, create one automatically
      let id = ticketId;
      if (!id) {
        await startNewTicket();
        // startNewTicket sets ticketId async; fetch latest from state after short tick
        id = ticketId;
      }
      const realId = id || ticketId;
      if (!realId) return;

      setText("");

      await addDoc(collection(db, "supportTickets", realId, "messages"), {
        from: "USER",
        uid,
        name: meName,
        text: t,
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "supportTickets", realId), {
        status: "OPEN", // user message re-opens attention
        lastMessageText: t,
        lastMessageAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    } catch (e) {
      console.error(e);
      setErr("Failed to send. Check rules/index.");
      setText(t); // restore
    }
  }

  const headerTitle = ticket?.subject || "Support Chat";

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button onClick={goBack} style={S.back}>←</button>
        <div>
          <div style={S.h1}>{headerTitle}</div>
          <div style={S.sub}>Chat with Support (real-time)</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={S.badge}>{ticket?.status || "NEW"}</span>
        </div>
      </div>

      {!ticketId ? (
        <div style={S.card}>
          <div style={S.label}>Start a new support chat</div>
          <input
            style={S.input}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (e.g. Withdrawal issue)"
          />
          <button style={S.primaryBtn} onClick={startNewTicket} disabled={starting}>
            {starting ? "Starting…" : "Start chat"}
          </button>
          {err ? <div style={S.err}>{err}</div> : null}
          <div style={S.tip}>
            Tip: after you start, you’ll see a chat box like WhatsApp.
          </div>
        </div>
      ) : (
        <>
          <div style={S.chatWrap}>
            {loading ? (
              <div style={S.card}>Loading…</div>
            ) : (
              <div style={S.chat}>
                {messages.length === 0 ? (
                  <div style={S.empty}>No messages yet. Say hi 👋</div>
                ) : (
                  messages.map((m) => {
                    const mine = m.from === "USER";
                    const staff = m.from === "STAFF";
                    const system = m.from === "SYSTEM";
                    return (
                      <div
                        key={m.id}
                        style={{
                          ...S.bubbleRow,
                          justifyContent: mine ? "flex-end" : "flex-start",
                        }}
                      >
                        <div
                          style={{
                            ...S.bubble,
                            ...(mine ? S.bubbleMe : null),
                            ...(staff ? S.bubbleStaff : null),
                            ...(system ? S.bubbleSystem : null),
                          }}
                        >
                          <div style={S.bubbleText}>{m.text}</div>
                          <div style={S.bubbleMeta}>
                            {system ? "system" : staff ? "support" : "you"} · {fmtTime(m.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>
            )}
          </div>

          {err ? <div style={S.errBar}>{err}</div> : null}

          <div style={S.composer}>
            <input
              style={S.composeInput}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your message…"
              onKeyDown={(e) => {
                if (e.key === "Enter") send();
              }}
            />
            <button style={S.sendBtn} onClick={send}>
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background: "linear-gradient(180deg, #f7f8ff 0%, #eef2ff 45%, #f8fafc 100%)",
    display: "flex",
    flexDirection: "column",
  },
  top: {
    maxWidth: 900,
    margin: "0 auto 12px",
    display: "flex",
    gap: 12,
    alignItems: "center",
    width: "100%",
  },
  back: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
  },
  h1: { fontSize: 18, fontWeight: 950, color: "#0f172a" },
  sub: { marginTop: 2, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.60)" },
  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    fontSize: 12,
    fontWeight: 900,
    color: "#0f172a",
  },

  card: {
    maxWidth: 900,
    width: "100%",
    margin: "0 auto",
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
  },
  label: { fontWeight: 950, marginBottom: 8, color: "#0f172a" },
  input: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 800,
  },
  primaryBtn: {
    marginTop: 10,
    width: "100%",
    padding: "12px 14px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, #6366f1 0%, #ec4899 100%)",
    color: "white",
    fontWeight: 950,
    cursor: "pointer",
  },
  tip: {
    marginTop: 10,
    fontSize: 12,
    fontWeight: 800,
    color: "rgba(15,23,42,0.65)",
  },

  chatWrap: {
    maxWidth: 900,
    width: "100%",
    margin: "0 auto",
    flex: 1,
    display: "flex",
    minHeight: 0,
  },
  chat: {
    flex: 1,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.05)",
    padding: 12,
    overflowY: "auto",
  },
  empty: {
    padding: 14,
    borderRadius: 14,
    background: "rgba(2,6,23,0.03)",
    border: "1px dashed rgba(15,23,42,0.15)",
    fontWeight: 900,
    color: "rgba(15,23,42,0.70)",
  },

  bubbleRow: { display: "flex", marginBottom: 10 },
  bubble: {
    maxWidth: "78%",
    borderRadius: 16,
    padding: "10px 12px",
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
  },
  bubbleMe: {
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(99,102,241,0.22)",
  },
  bubbleStaff: {
    background: "rgba(34,197,94,0.10)",
    border: "1px solid rgba(34,197,94,0.18)",
  },
  bubbleSystem: {
    background: "rgba(2,6,23,0.04)",
    border: "1px solid rgba(2,6,23,0.08)",
  },
  bubbleText: { fontWeight: 850, color: "#0f172a", lineHeight: 1.35 },
  bubbleMeta: { marginTop: 6, fontSize: 11, fontWeight: 900, color: "rgba(15,23,42,0.55)" },

  composer: {
    maxWidth: 900,
    width: "100%",
    margin: "12px auto 0",
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  composeInput: {
    flex: 1,
    padding: "12px 12px",
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 850,
    background: "rgba(255,255,255,0.95)",
  },
  sendBtn: {
    padding: "12px 14px",
    borderRadius: 16,
    border: "none",
    background: "linear-gradient(135deg, #6366f1 0%, #ec4899 100%)",
    color: "white",
    fontWeight: 950,
    cursor: "pointer",
    minWidth: 92,
  },

  err: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.18)",
    background: "rgba(239,68,68,0.08)",
    fontWeight: 900,
    color: "rgba(127,29,29,0.95)",
  },
  errBar: {
    maxWidth: 900,
    width: "100%",
    margin: "10px auto 0",
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.18)",
    background: "rgba(239,68,68,0.08)",
    fontWeight: 900,
    color: "rgba(127,29,29,0.95)",
  },
};
